// Classroom utility functions

/**
 * Generates a color class for classroom badges based on index
 * @param {number} index - The index of the classroom
 * @returns {string} - Tailwind CSS classes for background and text color
 */
export const getClassroomColor = (index) => {
  const colors = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-red-100 text-red-800',
    'bg-yellow-100 text-yellow-800',
    'bg-indigo-100 text-indigo-800',
    'bg-pink-100 text-pink-800',
    'bg-teal-100 text-teal-800',
    'bg-cyan-100 text-cyan-800',
    'bg-lime-100 text-lime-800',
    'bg-amber-100 text-amber-800',
    'bg-emerald-100 text-emerald-800',
    'bg-violet-100 text-violet-800',
    'bg-rose-100 text-rose-800',
    'bg-slate-100 text-slate-800',
  ];
  return colors[index % colors.length];
};

/**
 * Formats a classroom's subjects into a readable string
 * @param {Array} subjects - Array of subject objects
 * @returns {string} - Comma-separated string of subject names
 */
export const formatSubjects = (subjects) => {
  if (!subjects || !Array.isArray(subjects) || subjects.length === 0) {
    return 'No subjects';
  }
  return subjects.map(subject => subject.name || subject).join(', ');
};

/**
 * Generates a unique class UID if one isn't provided
 * @param {string} className - The name of the class
 * @returns {string} - A unique identifier
 */
export const generateClassUID = (className) => {
  if (!className) return '';
  
  // Take first two letters of each word, uppercase
  const words = className.trim().split(/\s+/);
  let uid = '';
  
  if (words.length === 1) {
    uid = words[0].substring(0, 2).toUpperCase();
  } else {
    uid = words.map(word => word.charAt(0).toUpperCase()).join('').substring(0, 3);
  }
  
  // Add random number to ensure uniqueness
  const randomSuffix = Math.floor(Math.random() * 100).toString().padStart(2, '0');
  return `${uid}${randomSuffix}`;
};

/**
 * Validates classroom form data
 * @param {Object} formData - The form data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateClassroomForm = (formData) => {
  const errors = {};
  
  if (!formData.class_name || formData.class_name.trim().length === 0) {
    errors.class_name = 'Class name is required';
  }
  
  if (!formData.subjects || formData.subjects.length === 0) {
    errors.subjects = 'At least one subject is required';
  }
  
  // Optional: Validate class_uid format if provided
  if (formData.class_uid && !/^[A-Z0-9]{2,6}$/.test(formData.class_uid)) {
    errors.class_uid = 'Class UID should be 2-6 uppercase letters and/or numbers';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Formats classroom data for display
 * @param {Object} classroom - Raw classroom data from API
 * @returns {Object} - Formatted classroom data
 */
export const formatClassroomData = (classroom) => {
  return {
    id: classroom.id || classroom._id,
    name: classroom.name || classroom.class_name,
    uid: classroom.class_uid || classroom.uid,
    subjects: formatSubjects(classroom.subjects),
    staffCount: classroom.staff_count || classroom.staffMembers?.length || 0,
    studentCount: classroom.student_count || classroom.students?.length || 0,
    archived: Boolean(classroom.archived),
    createdAt: classroom.createdAt || classroom.created_at,
    updatedAt: classroom.updatedAt || classroom.updated_at,
  };
};

/**
 * Filters classrooms based on search term and status
 * @param {Array} classrooms - Array of classroom objects
 * @param {string} searchTerm - Search term to filter by
 * @param {string} statusFilter - Status filter ('all', 'active', 'archived')
 * @returns {Array} - Filtered classrooms
 */
export const filterClassrooms = (classrooms, searchTerm = '', statusFilter = 'all') => {
  return classrooms.filter((classroom) => {
    const matchesSearch = !searchTerm || 
      classroom.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classroom.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classroom.subjects?.some(subject => 
        subject.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && !classroom.archived) ||
      (statusFilter === 'archived' && classroom.archived);
    
    return matchesSearch && matchesStatus;
  });
};

/**
 * Sorts classrooms by a given field
 * @param {Array} classrooms - Array of classroom objects
 * @param {string} sortBy - Field to sort by ('name', 'created', 'students', 'staff')
 * @param {string} sortOrder - Sort order ('asc' or 'desc')
 * @returns {Array} - Sorted classrooms
 */
export const sortClassrooms = (classrooms, sortBy = 'name', sortOrder = 'asc') => {
  return [...classrooms].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = (a.name || a.class_name || '').toLowerCase();
        bValue = (b.name || b.class_name || '').toLowerCase();
        break;
      case 'created':
        aValue = new Date(a.createdAt || a.created_at || 0);
        bValue = new Date(b.createdAt || b.created_at || 0);
        break;
      case 'students':
        aValue = a.student_count || a.students?.length || 0;
        bValue = b.student_count || b.students?.length || 0;
        break;
      case 'staff':
        aValue = a.staff_count || a.staffMembers?.length || 0;
        bValue = b.staff_count || b.staffMembers?.length || 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};